import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { SimpleTopic, TmfPublishing, Topic, TopicField } from '../utils/topic-utils';
import { getServerConfig } from '../server-config';
import { ErrorCode } from '../constants/error-codes';
import { ErrorSeverity } from '@app/services/dialogs/components/errors-information/errors-information.component';
import { FieldsLevelEnum } from '@app/session/default-forms';

import { FieldOptions } from '@app/origami-api/models/field.models';

export enum FormType {
  defaultForm = 1,
  customForm = 2,
  domainSearchableFieldsForm = 3,
  searchableForm = 4,
  mixedForm = 6,
}

export namespace ConfigServiceOptions {
  export enum ModuleName {
    // it is for columns settings service
    GridConfiguration = 'GridConfiguration',
    ViewModeConfiguration = 'ViewModeConfiguration',
    UiConfiguration = 'UiConfiguration',

    DocumentView = 'DocumentView',

    // its modules have no settings classes
    Coding = 'Coding',
    Answer = 'Answer',
    User = 'User',
    Site = 'Site',
    Country = 'Country',
    InstitutionalReviewBoard = 'InstitutionalReviewBoard',
    HealthAgency = 'HealthAgency',
    SubmissionProfile = 'SubmissionProfile',
    CommunicationLog = 'CommunicationLog',
    AuditInformation = 'AuditInformation',
    FaqView = 'FaqView',
    Collaboration = 'Collaboration',
    Users = 'Users',
    Tasks = 'Tasks',
    Templates = 'Templates',
    ConfidentialityAgreement = 'ConfidentialityAgreement',
    CountryInDataRoom = 'CountryInDataRoom',
    Organizations = 'Organizations',
    DocumentTypes = 'DocumentTypes',
    IncidentTypes = 'IncidentTypes',
    CapaTypes = 'CapaTypes',
    ActionItemTypes = 'ActionItemTypes',
    AuditTypes = 'AuditTypes',
    FindingTypes = 'FindingTypes',
    SupplierTypes = 'SupplierTypes',
    Email = 'Email',
    General = 'General',
    InvestigativeSites = 'InvestigativeSites',
    Notifications = 'Notifications',
    Program = 'Program',
    Question = 'Question',
    Reports = 'Reports',
    Security = 'Security',
    Workflow = 'Workflow',
    ManagerAvailableModules = 'Manager.AvailableModules',
    Translations = 'Transport',
    Labels = 'Labels',
  }

  export interface ExportSiteSettingsParams {
    ExportFormat: number;
    ExportOptions: {
      IncludeIndexFolders: 1 | 0;
    };
  }

  export interface GetDefaultFormsOptions {
    siteId: number;
    topicTypeIds: number[];
    tmfPublishing?: TmfPublishing;
  }

  export interface SearchCustomFormsOptions {
    conditions?: ConfigServiceModel.CustomFieldsFormCondition[];
    fields?: TopicField[];
    siteId: number;
    topicTypeId: number;
    topicId?: number;
    tmfPublishing?: TmfPublishing;
  }

  export interface GetCustomFieldsFormOptions {
    formId: number;
    formTypeId?: FormType;
    siteId?: number;
    topicTypeId?: number;
  }

  export interface GetMainPatternInsertionsOptions {
    patternTypeId: number;
    siteId?: number;
  }

  export interface GetPatternPreviewOptions {
    patternTypeId: number;
    siteId?: number;
    pattern: string;
  }
  export interface UpdateDefaultFormOptions {
    siteId: number;
    topicTypeId: number;
    defaultForm: ConfigServiceModel.Form;
    deletedFields: any[];
  }
  export interface RelatedFormsOptions {
    siteId: number;
    relatedForms: {
      TopicTypeId: number;
      SubTypeId: number;
    }[];
  }

  export interface LockUnlockCustomFieldsFormOptions {
    siteId: number;
    formId: number;
    isLock: boolean;
    force: boolean;
  }

  export interface DeleteCustomFieldsFormOptions {
    siteId: number;
    formId: number;
  }
}

export namespace ConfigServiceModel {
  export interface Form {
    FormId: number;
    FormName: string;
    FormTypeId: number;
    TopicTypeId: number;
    SiteId: number;
    LockedByName?: string;
    IsInherited?: boolean;
    Conditions?: ConfigServiceModel.CustomFieldsFormCondition[];
    FieldsDependencies?: ConfigServiceModel.Dependency[];
    Fields?: ConfigServiceModel.Field[]; // inconsistency in the BE model
    FormFields?: ConfigServiceModel.Field[];
    Collections?: (ConfigServiceModel.Group | ConfigServiceModel.Section)[];
    RelatedForms?: {
      FieldsCount: number;
      FormId: number;
      FormName: string;
      IsSubForm: boolean;
      TopicTypeId: number;
    }[];
    Module?: string;
    IsSubForm?: boolean;
    TemplateTopicTypeId?: number | null;
    TemplateFormId?: number | null;
  }

  export enum FormCollectionType {
    Section = 0,
    Group = 1,
  }

  export enum FormSectionAppearance {
    Accordion = 0,
    Plain = 1,
  }

  export const DEFAULT_SECTION_NAME = 'section-default';
  export const DEFAULT_SECTION: ConfigServiceModel.Section = {
    CollectionId: -1,
    Name: DEFAULT_SECTION_NAME,
    IsDefault: true,
    Type: FormCollectionType.Section,
    Appearance: FormSectionAppearance.Plain,
    Title: '',
    SortOrder: 0,
    FormId: -1,
  };

  export interface Collection {
    Title: string;
    CollectionId: number;
    SortOrder: number;
    Name: string; // uniq
    FormId: number;
  }

  export interface Section extends Collection {
    IsDefault?: boolean;
    Appearance: FormSectionAppearance;
    ParentId?: null;
    Type: FormCollectionType.Section;
  }

  export interface Group extends Collection {
    ParentId: number;
    Width: number;
    Type: FormCollectionType.Group;
  }

  export class Field {
    FieldName: string;
    Fieldvalue?: string;
    Flags: number;
    Title: string;
    Description: string;
    CategoryIds: string;
    SortOrder: number;
    FacetSortOrder?: number;
    DefaultTitle: string;
    HasSqlIndex: boolean;
    IsUsedInCustomForms: boolean;
    RelatedTo: string;
    FieldNonChangeableFlags: number;
    NonChangeableFlags?: number;
    LookupFieldValues: { Value: string; Name?: string }[];
    KeywordSetId: number;
    UIData?: any;
    Options?: FieldOptions;
    UpdateReason?: string;
    CollectionId?: number;
    Width?: number;
    Level?: FieldsLevelEnum;
  }

  export interface CustomFieldsFormCondition {
    FieldName: string;
    FieldValue: string;
  }

  export interface Condition {
    Title: string;
    ConditionId: number;
    FieldName: string;
    FieldValue: string;
  }

  export interface Action {
    ConditionId: number;
    FieldName: string;
    SetFlags: number;
    ResetFlags: number;
    SetValue: string;
    SetValueFrom: string;
  }

  export interface Dependency {
    Title: string;
    Condition: Condition;
    Actions: Action[];
  }

  export interface ErrorCategory {
    Key: string;
    Value: {
      ErrorCode: number;
      Errors: {
        ErrorCode: number;
        ObjectId: number;
        ObjectType: string;
        RelatedObjectType: string;
        Severity?: ErrorSeverity;
      }[];
    };
  }

  export interface ErrorsArray {
    // TODO: make correct interface
    Key: string;
    Value: {
      ErrorCode: number;
      Errors: {
        ErrorCode: number;
        ObjectId: number;
        ObjectType: string;
        RelatedObjectType: string;
      }[];
    };
  }

  export interface ExportSiteSettingsResult {
    VirtualFilePath: string;
    ExportResult: ExportResult;
  }

  export interface ExportResult {
    AuditSettingsResults: ErrorCategory[];
    CalendarTaskSettingsResults: ErrorCategory[];
    ConfidentialityAgreementSettingsResult: ErrorCategory[];
    CountriesSettingsResults: ErrorCategory[];
    CustomFieldsDependenciesSettingsResults: ErrorCategory[];
    DocumentTypeSettingsResults: ErrorCategory[];
    DocumentViewSettingsResult: ErrorCategory[];
    EmailSettingsResult: ErrorCategory[];
    FaqSettingsResult: ErrorCategory[];
    FoldersSecuritySettingsResult: ErrorCategory[];
    FormsSettingsResults: ErrorCategory[];
    GeneralSettingsResults: ErrorCategory[];
    IndexSettingsResult: ErrorCategory[];
    InvSiteCloningResults: ErrorCategory[];
    InvestigativeSitesSettingsResults: ErrorCategory[];
    MainErrors: ErrorCategory[];
    MilestonesSettingsResults: ErrorCategory[];
    NotificationsSettingsResult: ErrorCategory[];
    QASettingsResult: ErrorCategory[];
    ReportsSettingsResult: ErrorCategory[];
    RequiredDocumentsSettingsResult: ErrorCategory[];
    SecuritySettingsResult: ErrorCategory[];
    WorkflowSettingsResults: ErrorCategory[];
  }

  export interface MainPatternInsertion {
    FieldInsertions: FieldInsertion[];
    Functions: Function[];
    PatternInsertion: PatternInsertion[];
  }

  export interface FieldInsertion {
    FieldsList: {
      DummyValue: string;
      FieldName: string;
      FieldTitle: string;
    }[];
    TopicTypeName: string;
    TopicTypeShortName: string;
  }

  export interface Function {
    Description: string;
    Example: string;
    FunctionName: string;
    ParametersFunction: {
      Description: string;
      ParameterName: string;
      ParameterOrder: number;
      ParameterPossibleValue: string[];
      ParameterType: string;
    }[];
  }

  export interface PatternInsertion {
    InsertionDescription: string;
    InsertionId: number;
    InsertionName: string;
    PatternTypeId: number;
    TemplateVal: string;
  }

  export interface PatternPreview {
    Preview: string;
  }
}

export interface Options {
  options?: any;
  silentErrorCodes?: ErrorCode[];
}

export interface AiFlag {
  BitValue?: number;
  Checks?: AiFlagChecks[];
  ClientFilters?: number[];
  CreatedDate?: string;
  DocumentTypeIds?: number[];
  Enabled: boolean;
  FlagId: number;
  IsSupportedByLibrary?: boolean;
  LastUpdated?: string;
  Name: string;
  RoomTypeFilters: number[];
  SeverityId: number;
  StatusIdDocumentFieldName?: string;
  Title: string;
}

export interface AiFlagChecks {
  AiFlagCheckId: number;
  AiFlagId: number;
  Code: string;
  CreatedDate: string;
  Description: string;
  IsSupportedByLibrary: boolean;
  LastUpdated: string;
  Name: string;
}

export interface GetSiteAiFlagsOptions {
  siteId: number;
  flagId?: number[];
  clientFilterIds?: number[];
  roomTypeFilterIds?: number[];
  severityIds?: number[];
  orderByClause?: string;
}

export interface SiteAiFlag {
  AiFlagTypeId: number;
  ClientFilters: number[];
  CreatedBy: number;
  CreatedDate: string;
  DocumentTypeIds: number[];
  Enabled: boolean;
  FlagId: number;
  Name: string;
  Title: string;
  LastUpdated: string;
  LastUpdatedBy: number;
  RoomTypeFilters: number[];
  SeverityId: number;
  SiteId: number;
  WorkflowIssueId: number;
  WorkflowIssueName: string;
}

@Injectable()
export class ConfigService extends ServiceBase {
  serviceUrl = 'ConfigService.svc';

  getSettings(
    roomId: number,
    modules: ConfigServiceOptions.ModuleName[] = [],
    settings: string[] = [],
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
    ...params
  ): Observable<OrigamiResponse<Topic[]>> {
    const body = {
      domain: getServerConfig().domain,
      siteId: roomId,
      module: modules.join(),
      name: settings.join(),
    };
    return this.origamiPost('/GetSettings', body, null, silentErrorCodes, forceRequest, ...params);
  }

  setSettings(
    siteId: number,
    settings: SimpleTopic[],
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    const body = {
      siteId,
      settings,
    };
    return this.origamiPost('/SetSettings', body, null, silentErrorCodes, true);
  }

  getDefaultForms(
    body: ConfigServiceOptions.GetDefaultFormsOptions,
    silentErrorCodes?: ErrorCode[],
    force = false,
  ): Observable<OrigamiResponse<ConfigServiceModel.Form[]>> {
    return this.origamiPost('/GetDefaultForms', body, null, silentErrorCodes, force);
  }

  getCustomFieldsForm(
    params: ConfigServiceOptions.GetCustomFieldsFormOptions,
    options?: unknown,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ConfigServiceModel.Form>> {
    return this.origamiPost(
      '/GetCustomFieldsForm',
      {
        formId: params.formId,
        formTypeId: params.formTypeId,
        siteId: params.siteId,
        topicTypeId: params.topicTypeId,
      },
      options,
      silentErrorCodes,
      forceRequest,
    );
  }

  exportSiteSettings(
    siteId: number,
    param: ConfigServiceOptions.ExportSiteSettingsParams,
  ): Observable<OrigamiResponse<ConfigServiceModel.ExportSiteSettingsResult>> {
    const body = {
      siteId,
      param,
    };
    return this.origamiPost('/ExportSiteSettings', body, null, null, true);
  }

  getTimeZones(...params) {
    return this.origamiPost('/GetTimeZones', {}, ...params);
  }

  searchCustomForms(
    body: ConfigServiceOptions.SearchCustomFormsOptions,
    ...params
  ): Observable<OrigamiResponse<ConfigServiceModel.Form[]>> {
    return this.origamiPost('/SearchCustomForms', body, ...params);
  }

  getMainPatternInsertions(
    body: ConfigServiceOptions.GetMainPatternInsertionsOptions,
    ...params
  ): Observable<OrigamiResponse<ConfigServiceModel.MainPatternInsertion>> {
    return this.origamiPost('/GetMainPatternInsertions', body, ...params);
  }

  getPatternPreview(
    body: ConfigServiceOptions.GetPatternPreviewOptions,
  ): Observable<OrigamiResponse<ConfigServiceModel.PatternPreview>> {
    return this.origamiPost('/GetPatternPreview', body, null, null, true);
  }

  updateDefaultForm(body: ConfigServiceOptions.UpdateDefaultFormOptions, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/UpdateDefaultForm', body, null, silentErrorCodes, true);
  }

  lockUnlockCustomFieldsForm(
    body: ConfigServiceOptions.LockUnlockCustomFieldsFormOptions,
    silentErrorCodes?: ErrorCode[],
  ) {
    return this.origamiPost('/LockUnlockCustomFieldsForm', body, null, silentErrorCodes, true);
  }

  deleteCustomFieldsForm(body: ConfigServiceOptions.DeleteCustomFieldsFormOptions, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/DeleteCustomFieldsForm', body, null, silentErrorCodes, true);
  }

  addRelatedForms(body: ConfigServiceOptions.RelatedFormsOptions, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/AddRelatedForms', body, null, silentErrorCodes, true);
  }

  removeRelatedForms(body: ConfigServiceOptions.RelatedFormsOptions, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/RemoveRelatedForms', body, null, silentErrorCodes, true);
  }

  getAiFlags(): Observable<OrigamiResponse<AiFlag[]>> {
    return this.origamiPost('/GetAiFlags', null, null, null, true);
  }

  getSiteAiFlags(body: GetSiteAiFlagsOptions): Observable<OrigamiResponse<SiteAiFlag[]>> {
    return this.origamiPost('/GetSiteAiFlags', body, null, null, true);
  }

  setSiteAiFlags(body: { siteId: number; flags: AiFlag[] }): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/SetSiteAiFlags', body, null, null, true);
  }

  setDomainAiFlags(body: { siteId: number; flags: AiFlag[] }): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/SetDomainAiFlags', body, null, null, true);
  }
}
